import { action, observable } from 'mobx';
import { observer } from 'mobx-react';
import { autoinject, bind, FormStore, inject, Input, Loader, TasksStore } from 'Mri.Web.Common';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { AccountStore } from '../Account/AccountStore';
import { AppStore } from '../Core/AppStore';
import { ManageStore } from './ManageStore';

export interface ILookAndFeelProps extends RouteComponentProps<undefined> {}

@autoinject
@observer
export class LookAndFeel extends React.Component<ILookAndFeelProps> {
    public async componentDidMount() {
        await this.onLoad();
    }

    public render() {
        return (
            <div className="container-fluid">
                <Loader message="Loading Settings..." showSpinner center loading={this.loading} />

                {this.userSettingsStore && (
                    <div className="row">
                        <div className="col-12">
                            <a className="btn btn-primary float-end" onClick={this.updateSettings}>
                                {this.updating ? 'Updating' : 'Update Settings'}
                            </a>
                        </div>
                        <div className="col-md-6 mb-3">
                            <h4 className="ps-4">Theming</h4>
                            <table className="interface-bg">
                                <tbody>
                                    <tr>
                                        <td className="d-table-cell p-2">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                checked={
                                                    this.userSettingsStore.fields.preferredTheme.value == 'theme-1'
                                                }
                                                value="theme-1"
                                                onChange={this.onChangeTheme}
                                            />
                                        </td>
                                        <td className="d-table-cell bg-teal w-25 rounded-start dt-border-left p-3" />
                                        <td className="d-table-cell bg-white w-25 border dt-border-y p-3" />
                                        <td className="d-table-cell bg-gray-darker w-25 rounded-end dt-border-right p-3" />
                                    </tr>

                                    <tr className="separator" />

                                    <tr>
                                        <td className="d-table-cell p-2">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                checked={
                                                    this.userSettingsStore.fields.preferredTheme.value == 'theme-2'
                                                }
                                                value="theme-2"
                                                onChange={this.onChangeTheme}
                                            />
                                        </td>

                                        <td className="d-table-cell bg-red w-25 rounded-start dt-border-left p-3" />
                                        <td className="d-table-cell bg-white w-25 border dt-border-y p-3" />
                                        <td className="d-table-cell bg-gray-darker w-25 rounded-end dt-border-right p-3" />
                                    </tr>

                                    <tr className="separator" />

                                    <tr className="d-table-row">
                                        <td className="d-table-cell p-2">
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                checked={
                                                    this.userSettingsStore.fields.preferredTheme.value == 'dark-theme'
                                                }
                                                value="dark-theme"
                                                onChange={this.onChangeTheme}
                                            />
                                        </td>

                                        <td className="d-table-cell bg-teal w-25 rounded-start dt-border-left p-3" />
                                        <td className="d-table-cell bg-gray-dark w-25 dt-border-y p-3" />
                                        <td className="d-table-cell bg-gray-darker w-25 rounded-end dt-border-right p-3" />
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div className="col-md-6">
                            <h4 className="ps-4">Settings</h4>
                            <div>
                                <Input
                                    type="checkbox"
                                    className="ms-0"
                                    field={this.userSettingsStore.fields.statusVuNewTab}
                                />
                                Open StatusVu in New Tab
                                <br />
                                <Input
                                    type="checkbox"
                                    className="ms-0"
                                    field={this.userSettingsStore.fields.fahrenheitPreferred}
                                />
                                Farenheit Temp Preferred
                                <br />
                                <Input
                                    type="checkbox"
                                    className="ms-0"
                                    field={this.userSettingsStore.fields.multiSortPreferred}
                                />
                                Multi Sort Preferred
                            </div>

                            <br />

                            <h4 className="ps-4">TimeZone Preference</h4>
                            <div>
                                <input
                                    type="radio"
                                    className="form-check-input me-2"
                                    checked={this.userSettingsStore.fields.preferredTimezone.value == 'unit-time'}
                                    value="unit-time"
                                    onChange={this.onChangeTimezone}
                                />
                                Local Unit Time
                            </div>

                            <div>
                                <input
                                    type="radio"
                                    className="form-check-input me-2"
                                    checked={this.userSettingsStore.fields.preferredTimezone.value == 'user-time'}
                                    value="user-time"
                                    onChange={this.onChangeTimezone}
                                />
                                Local User Time
                            </div>

                            <div>
                                <input
                                    type="radio"
                                    className="form-check-input me-2"
                                    checked={this.userSettingsStore.fields.preferredTimezone.value == 'utc'}
                                    value="utc"
                                    onChange={this.onChangeTimezone}
                                />
                                UTC
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    @bind
    @action
    private async onLoad() {
        this.loading = true;

        try {
            await this.accountStore.loadUserAsync();

            const user = this.accountStore.user;

            if (user) {
                this.userSettingsStore = new FormStore<Mri.Web.Portal.Controllers.Manage.ChangeUserSettingsModel>({
                    preferredTheme: user.preferredTheme,
                    viewedChangelog: user.viewedChangelog,
                    metaData: user.metaData,
                    statusVuNewTab: user.statusVuNewTab,
                    fahrenheitPreferred: user.fahrenheitPreferred,
                    preferredTimezone: user.preferredTimezone,
                    multiSortPreferred: user.multiSortPreferred,
                    subscribedToAlerts: user.subscribedToAlerts,
                });
            }
        } catch (e: any) {
            this.appStore.alertHandle.setError(e);
        } finally {
            this.loading = false;
        }
    }

    @bind
    @action
    private async onChangeTheme(event: React.ChangeEvent<HTMLInputElement>) {
        const selectedTheme = event.currentTarget.value;
        this.appStore.storage.theme = selectedTheme as any;
        this.userSettingsStore!.fields.preferredTheme.setValue(selectedTheme);
    }

    @bind
    @action
    private async onChangeTimezone(event: React.ChangeEvent<HTMLInputElement>) {
        const selectedTimezone = event.currentTarget.value;
        this.userSettingsStore!.fields.preferredTimezone.setValue(selectedTimezone);
    }

    @bind
    @action
    private async updateSettings(event: React.MouseEvent<HTMLAnchorElement>) {
        this.updating = true;

        const task = this.tasksStore.startSessionTask('Update Settings');
        try {
            await this.manageStore.changeSettingsAsync(this.userSettingsStore!.values);
            task.complete();
        } catch (e: any) {
            task.error(e);
        } finally {
            this.updating = false;
        }
    }

    @observable
    private loading = false;
    @observable
    private updating = false;
    @observable
    private userSettingsStore?: FormStore<Mri.Web.Portal.Controllers.Manage.ChangeUserSettingsModel>;
    @inject
    private manageStore!: ManageStore;
    @inject
    private appStore!: AppStore;
    @inject
    private accountStore!: AccountStore;
    @inject
    private tasksStore!: TasksStore;
}
